import { FC, HTMLAttributes, useCallback } from 'react';

import _ from '../../locale';
import { styled } from '../../theme';
import SvgIcon from '../SvgIcon';
import { FlexRow } from '../index';
import PageNumberControl from './PageNumberControl';
import PaginationControlButton from './PaginationControlButton';
import { clampPage, getSafePageCount } from './utils';

const Container = styled(FlexRow)({
    margin: '20px auto',
});

const RightIcon = styled(SvgIcon)({
    transform: 'scaleX(-1)',
});

type Props = Omit<HTMLAttributes<HTMLElement>, 'onChange'> & {
    currentPage: number;
    rowsPerPage: number;
    totalItems: number;
    onChangePage: (page: number) => void;
    compact?: boolean;
};

const Pagination: FC<Props> = ({
    totalItems,
    currentPage,
    onChangePage,
    rowsPerPage,
    compact = false,
    'aria-label': ariaLabel = 'Pagination',
    ...restProps
}) => {
    const pageCount = getSafePageCount(totalItems, rowsPerPage);
    const safeCurrentPage = clampPage(currentPage, pageCount);
    const isFirstPage = safeCurrentPage <= 1;
    const isLastPage = safeCurrentPage >= pageCount;

    const changePage = useCallback(
        (page: number) => {
            const nextPage = clampPage(page, pageCount);

            if (nextPage !== currentPage) {
                onChangePage(nextPage);
            }
        },
        [currentPage, onChangePage, pageCount]
    );

    const handleFirstClick = useCallback(() => {
        changePage(1);
    }, [changePage]);

    const handlePrevClick = useCallback(() => {
        changePage(safeCurrentPage - 1);
    }, [changePage, safeCurrentPage]);

    const handleNextClick = useCallback(() => {
        changePage(safeCurrentPage + 1);
    }, [changePage, safeCurrentPage]);

    const handleLastClick = useCallback(() => {
        changePage(pageCount);
    }, [changePage, pageCount]);

    if (rowsPerPage >= totalItems) {
        return null;
    }

    return (
        <Container
            {...restProps}
            aria-label={ariaLabel}
            as="nav"
            align="center"
            justify="center"
            gap={8}
            style={compact ? { margin: 0, ...restProps.style } : restProps.style}
        >
            {!compact && (
                <PaginationControlButton
                    aria-label="First page"
                    disabled={isFirstPage}
                    onClick={handleFirstClick}
                    type="button"
                >
                    {_('pagination_first_page')}
                </PaginationControlButton>
            )}
            <PaginationControlButton
                aria-label="Previous page"
                disabled={isFirstPage}
                onClick={handlePrevClick}
                type="button"
                variant="icon"
            >
                <SvgIcon alt="" src="/icons/ic-arrow.svg" size="lg" />
            </PaginationControlButton>
            {!compact && (
                <PageNumberControl
                    currentPage={safeCurrentPage}
                    pageCount={pageCount}
                    onChangePage={changePage}
                />
            )}
            <PaginationControlButton
                aria-label="Next page"
                disabled={isLastPage}
                onClick={handleNextClick}
                type="button"
                variant="icon"
            >
                <RightIcon alt="" src="/icons/ic-arrow.svg" size="lg" />
            </PaginationControlButton>
            {!compact && (
                <PaginationControlButton
                    aria-label="Last page"
                    disabled={isLastPage}
                    onClick={handleLastClick}
                    type="button"
                >
                    {_('pagination_last_page')}
                </PaginationControlButton>
            )}
        </Container>
    );
};

export default Pagination;
